import React from "react";

import { SALES_STAGES, dealAmount, formatCurrency, isOverdue, stageColor } from "./constants";

// Stats on the deals on screen, so the people squares and the filters above apply here too.
export default function Stats({ deals, budgetsByProject, selectedPersonId, onSelectPerson }) {
  return (
    <div className="space-y-6">
      <PeopleStats deals={deals} budgetsByProject={budgetsByProject} selectedPersonId={selectedPersonId} onSelectPerson={onSelectPerson} />
      <Funnel deals={deals} budgetsByProject={budgetsByProject} />
    </div>
  );
}

// One row per sales or closing owner. A person holding both roles on a deal counts that deal once.
const PeopleStats = ({ deals, budgetsByProject, selectedPersonId, onSelectPerson }) => {
  const byId = {};
  for (const deal of deals) {
    const { amount, weighted } = dealAmount(deal, budgetsByProject[deal._id]);
    const roles = [
      { role: "sales", id: deal.sales_owner_id, name: deal.sales_owner_name, avatar: deal.sales_owner_avatar },
      { role: "closing", id: deal.closing_owner_id, name: deal.closing_owner_name, avatar: deal.closing_owner_avatar },
    ].filter((r) => r.id);

    const seen = new Set();
    for (const r of roles) {
      if (!byId[r.id]) {
        byId[r.id] = { _id: r.id, name: r.name, avatar: r.avatar, deals: 0, sales: 0, closing: 0, amount: 0, weighted: 0, overdue: 0, blocked: 0, noAction: 0 };
      }
      const row = byId[r.id];
      row[r.role] += 1;
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      row.deals += 1;
      row.amount += amount || 0;
      row.weighted += weighted || 0;
      if (isOverdue(deal.next_action_at)) row.overdue += 1;
      if (deal.blocker) row.blocked += 1;
      if (!deal.next_action_at) row.noAction += 1;
    }
  }
  const rows = Object.values(byId).sort((a, b) => b.amount - a.amount);
  const unassigned = deals.filter((d) => !d.sales_owner_id && !d.closing_owner_id).length;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">By person</h2>
        <span className="text-xs text-gray-500">{unassigned} deals with no sales or closing owner</span>
      </div>
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            <th className="px-4 py-2 text-left">Person</th>
            <th className="px-3 py-2 text-right">Deals</th>
            <th className="px-3 py-2 text-right">As sales</th>
            <th className="px-3 py-2 text-right">As closing</th>
            <th className="px-3 py-2 text-right">Pipeline</th>
            <th className="px-3 py-2 text-right">Weighted</th>
            <th className="px-3 py-2 text-right">Overdue</th>
            <th className="px-3 py-2 text-right">Blocked</th>
            <th className="px-3 py-2 text-right">No next action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((p) => (
            <tr
              key={p._id}
              onClick={() => onSelectPerson(p._id)}
              className={`cursor-pointer ${selectedPersonId === p._id ? "bg-blue-50" : "hover:bg-gray-50"}`}>
              <td className="px-4 py-2">
                <div className="flex items-center gap-2">
                  {p.avatar ? <img src={p.avatar} alt={p.name} className="w-6 h-6 rounded object-cover" /> : <span className="w-6 h-6 rounded bg-gray-100" />}
                  <span className="font-medium text-gray-900 whitespace-nowrap">{p.name}</span>
                </div>
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{p.deals}</td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-700">{p.sales}</td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-700">{p.closing}</td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-900 whitespace-nowrap">{formatCurrency(p.amount)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-700 whitespace-nowrap">{formatCurrency(Math.round(p.weighted))}</td>
              <td className={`px-3 py-2 text-right tabular-nums ${p.overdue ? "text-red-600 font-semibold" : "text-gray-300"}`}>{p.overdue}</td>
              <td className={`px-3 py-2 text-right tabular-nums ${p.blocked ? "text-amber-700 font-semibold" : "text-gray-300"}`}>{p.blocked}</td>
              <td className={`px-3 py-2 text-right tabular-nums ${p.noAction ? "text-gray-900" : "text-gray-300"}`}>{p.noAction}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Deals and amount per stage. A deal with no stage sits in New, as on the kanban.
const Funnel = ({ deals, budgetsByProject }) => {
  const stages = SALES_STAGES.map((stage) => {
    const stageDeals = deals.filter((d) => (d.sales_stage || "new") === stage.value);
    const amount = stageDeals.reduce((sum, d) => sum + (dealAmount(d, budgetsByProject[d._id]).amount || 0), 0);
    return { ...stage, count: stageDeals.length, amount };
  });
  const maxCount = Math.max(1, ...stages.map((s) => s.count));

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
      <h2 className="text-sm font-semibold text-gray-900">By stage</h2>
      {stages.map((s) => (
        <div key={s.value} className="flex items-center gap-3">
          <span className={`w-28 shrink-0 px-2 py-0.5 rounded border text-xs font-medium ${stageColor(s.value)}`}>{s.label}</span>
          <div className="flex-1 h-5 bg-gray-50 rounded overflow-hidden">
            <div className="h-full bg-blue-500" style={{ width: `${(s.count / maxCount) * 100}%` }} />
          </div>
          <span className="w-16 text-right text-sm tabular-nums font-semibold text-gray-900">{s.count}</span>
          <span className="w-28 text-right text-sm tabular-nums text-gray-700 whitespace-nowrap">{formatCurrency(s.amount)}</span>
        </div>
      ))}
    </div>
  );
};
